/**
 * Workspace creation wizard — shared, scope-pinned mutable workspaces
 * (doc/arch/ASSETS.md + TEMPLATES.md). TWO TABS:
 *
 *   1. **Start from** — choose a StepFlow template OR upload your own .zip/.tar,
 *      AND (optionally) add curated open-source skills via the shared
 *      ``CatalogBrowser`` discover→select picker (multi-select).
 *   2. **Create** — mandatory Workspace name + the create action. On create:
 *        template → one call (server S3-copies the template + selected skills);
 *        upload   → create row → PUT archive → unpack (skills applied at unpack).
 *
 * Scope is AMBIENT — from ``registryScope`` (the catalog tab), never a field.
 * NB — distinct from the read-only Data Plane Asset (knowledge/RAG).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FileArchive, Loader2, UploadCloud, LayoutTemplate, Sparkles, X as XIcon } from 'lucide-react';
import {
  createWorkspace, updateWorkspace, uploadWorkspaceArchive, unpackWorkspace,
  listWorkspaceTemplates, listWorkspaceSkills, getWorkspaceSkillDetail,
  listSkills, getSkillDetail,
} from '../../services/toolsApi';
import CatalogBrowser from './CatalogBrowser.jsx';

const ALLOWED_EXTS = ['.zip', '.tar', '.tar.gz', '.tgz'];
const MAX_SKILLS = 12;

function extOk(name) {
  const n = String(name || '').toLowerCase();
  return ALLOWED_EXTS.some((e) => n.endsWith(e));
}

function validateLabel(label) {
  const s = String(label || '').trim();
  if (!s) return { ok: false, hint: 'Give this workspace a short, memorable name.' };
  if (s.length > 120) return { ok: false, hint: 'Use a shorter label (≤ 120 characters).' };
  return { ok: true, hint: '' };
}

function fmtBytes(n) {
  const b = Number(n) || 0;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

function scopeLabel(scope) {
  const k = String(scope?.kind || 'TENANT').toUpperCase();
  if (k === 'POOL') return `this room (${scope?.poolId || ''})`;
  if (k === 'NETWORK') return `this network (${scope?.networkId || ''})`;
  return 'the whole workspace (tenant)';
}

const TABS = [
  { key: 'start', title: 'Start from' },
  { key: 'create', title: 'Create' },
];

export default function ToolWorkspaceRegistryWizard({
  open,
  isNewEntry = true,
  registryScope,
  tenantId,
  initialEntry,        // EDIT mode: an existing workspace row { secretId, secretName, scope, provenance }
  onRegistryCreated,
  onCancel,
}) {
  // EDIT mode is driven by an existing workspace id. Source files (template /
  // archive) are immutable post-create — edit is the SKILLS layer only.
  const editingId = String((initialEntry && initialEntry.secretId) || '').trim();
  const isEdit = !isNewEntry && !!editingId;
  const [tab, setTab] = useState('start');
  const [label, setLabel] = useState('');
  const [source, setSource] = useState('template'); // 'template' | 'upload'
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [skills, setSkills] = useState([]);          // curated (imported) skills
  const [tenantSkills, setTenantSkills] = useState([]); // tenant-authored skills
  // CatalogBrowser uses a single id space; namespace each source so a curated
  // and a tenant skill with the same slug never collide ("curated:id"/"tenant:id").
  const [selectedNsIds, setSelectedNsIds] = useState([]);
  const [skillPickerOpen, setSkillPickerOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState(''); // 'uploading' | 'unpacking' | 'seeding'
  const [error, setError] = useState('');

  // Scope is DERIVED from where the wizard was opened (room → POOL, network →
  // NETWORK, tenant → TENANT) — NOT user-picked. This keeps creation authorization
  // implicit: the user can only reach this wizard from a surface they already
  // have access to, so they can only create at a scope they're entitled to. An
  // explicit picker would let someone choose a wider scope than their role allows
  // and 403 on save; the badge (read-only) is how scope is surfaced instead.
  const scope = useMemo(
    () => {
      // In EDIT mode the scope is the workspace's own (where it was created),
      // not the catalog tab's — a workspace can only be edited at its scope.
      if (isEdit && initialEntry?.scope && typeof initialEntry.scope === 'object') return initialEntry.scope;
      return registryScope && typeof registryScope === 'object' ? registryScope : { kind: 'TENANT' };
    },
    [isEdit, initialEntry, registryScope],
  );

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    listWorkspaceTemplates()
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res?.templates) ? res.templates : [];
        setTemplates(list);
        if (list.length === 0) setSource('upload');
        else setTemplateId((cur) => cur || list[0].id);
      })
      .catch(() => { if (!cancelled) { setTemplates([]); setSource('upload'); } });
    listWorkspaceSkills()
      .then((res) => { if (!cancelled) setSkills(Array.isArray(res?.skills) ? res.skills : []); })
      .catch(() => { if (!cancelled) setSkills([]); });
    // Authored skills (doc/arch/vertical/SKILLS.md + vertical/SKILLS.md). Slim row:
    // { secretId, secretName, description, selfImproving, scope }. When the
    // workspace is scoped to a NETWORK, pass networkId so the pyramid returns
    // that network's skills PLUS the tenant-wide ones — enabling "Add all from
    // network". Tenant-scoped workspace → tenant rows only.
    const wsNetworkId = String(scope?.networkId || '').trim();
    listSkills(wsNetworkId ? { networkId: wsNetworkId } : {})
      .then((res) => { if (!cancelled) setTenantSkills(Array.isArray(res?.secrets) ? res.secrets : []); })
      .catch(() => { if (!cancelled) setTenantSkills([]); });
    return () => { cancelled = true; };
  }, [open, scope]);

  // EDIT mode: seed the workspace name + its currently-baked skills (from
  // provenance) as the starting selection, and land straight on the skills tab.
  useEffect(() => {
    if (!open || !isEdit) return;
    setLabel(String(initialEntry?.secretName || '').trim());
    const prov = (initialEntry && (initialEntry.provenance
      || (initialEntry.config && initialEntry.config.provenance))) || {};
    const curated = Array.isArray(prov.skillIds) ? prov.skillIds : [];
    const tenant = (Array.isArray(prov.tenantSkills) ? prov.tenantSkills : [])
      .map((t) => String((t && (t.id ?? t)) || '').trim()).filter(Boolean);
    setSelectedNsIds([
      ...curated.map((id) => `curated:${id}`),
      ...tenant.map((id) => `tenant:${id}`),
    ]);
    setTab('start');
  }, [open, isEdit, initialEntry]);

  const labelCheck = validateLabel(label);

  const onPickFile = (f) => {
    if (!f) return;
    if (!extOk(f.name)) { setError(`Unsupported file type. Use one of: ${ALLOWED_EXTS.join(', ')}`); return; }
    setError(''); setFile(f);
  };

  // CatalogBrowser entries — TWO categories (tabs): curated "imported" skills
  // and the tenant's own. ``id`` is namespaced so the two id spaces can't clash;
  // ``rawId`` carries the real id for the create call; ``category`` drives tabs.
  const skillEntries = useMemo(() => {
    const curated = skills.map((s) => ({
      id: `curated:${s.id}`, rawId: s.id, category: 'curated',
      label: s.label, description: s.description, badge: s.license,
    }));
    const mine = tenantSkills.map((s) => ({
      id: `tenant:${s.secretId}`, rawId: s.secretId, category: 'tenant',
      label: s.secretName, description: s.description || '',
      badge: s.selfImproving ? 'self-improving' : 'tenant skill',
    }));
    return [...curated, ...mine];
  }, [skills, tenantSkills]);

  // NETWORK-scoped authored skills loaded for this workspace's network. Used by
  // "Add all from network" — a one-click bulk-select instead of ticking each.
  // (A network skill carries scope.kind === 'NETWORK'; tenant rows are excluded
  // so the bundle is exactly "this network's own skills".)
  const networkSkillNsIds = useMemo(
    () => tenantSkills
      .filter((s) => String(s?.scope?.kind || '').toUpperCase() === 'NETWORK')
      .map((s) => `tenant:${s.secretId}`),
    [tenantSkills],
  );
  const wsNetworkId = String(scope?.networkId || '').trim();
  const addAllFromNetwork = useCallback(() => {
    setSelectedNsIds((cur) => [...new Set([...cur, ...networkSkillNsIds])]);
  }, [networkSkillNsIds]);

  const skillTabs = useMemo(() => {
    const t = [];
    if (skills.length) t.push({ id: 'curated', label: 'Imported' });
    if (tenantSkills.length) t.push({ id: 'tenant', label: 'Your skills' });
    return t.length > 1 ? t : null; // only show tabs when both sources exist
  }, [skills.length, tenantSkills.length]);

  const loadSkillDetail = useCallback(async (entry) => {
    if (entry.category === 'tenant') {
      const d = await getSkillDetail(entry.rawId);
      return { body: d?.body || '' };
    }
    const d = await getWorkspaceSkillDetail(entry.rawId);
    return { body: d?.body || '' };
  }, []);
  const toggleSkill = useCallback((entry, now) => {
    const ns = entry.id; // namespaced
    setSelectedNsIds((cur) => (now ? [...new Set([...cur, ns])] : cur.filter((x) => x !== ns)));
  }, []);

  // "Start from" is ready once a source is chosen (template picked, or a valid
  // file). In EDIT mode the source is immutable, so it's always ready.
  const startReady = isEdit
    ? true
    : (source === 'template' ? !!templateId : (!!file && extOk(file.name)));

  // Split the namespaced selection back into the two id lists the API expects.
  const { skillIds, tenantSkillIds } = useMemo(() => {
    const c = []; const t = [];
    for (const ns of selectedNsIds) {
      if (ns.startsWith('curated:')) c.push(ns.slice('curated:'.length));
      else if (ns.startsWith('tenant:')) t.push(ns.slice('tenant:'.length));
    }
    return { skillIds: c, tenantSkillIds: t };
  }, [selectedNsIds]);

  const runCreate = useCallback(async () => {
    setBusy(true); setError('');
    try {
      let workspaceId = ''; let fileCount = 0; let sizeBytes = 0;
      if (source === 'template') {
        if (!templateId) throw new Error('Pick a template');
        setPhase('seeding');
        const created = await createWorkspace({ label: label.trim(), templateId, scope, skillIds, tenantSkillIds });
        workspaceId = String(created?.workspaceId || '').trim();
        if (!workspaceId) throw new Error('Could not create workspace from template');
        fileCount = created?.fileCount || 0; sizeBytes = created?.sizeBytes || 0;
      } else {
        if (!file) return;
        setPhase('uploading');
        const created = await createWorkspace({ label: label.trim(), filename: file.name, scope, skillIds, tenantSkillIds });
        workspaceId = String(created?.workspaceId || '').trim();
        if (!workspaceId || !created?.uploadUrl) throw new Error('Could not start upload');
        await uploadWorkspaceArchive(created.uploadUrl, file);
        setPhase('unpacking');
        const unpacked = await unpackWorkspace(workspaceId);
        fileCount = unpacked?.fileCount || 0; sizeBytes = unpacked?.sizeBytes || 0;
      }
      // Single event: created + done. No intermediate "success → Done" screen —
      // the wizard closes here via the caller's onRegistryCreated.
      onRegistryCreated?.({
        workspaceId, fileCount, sizeBytes,
        secretId: workspaceId, secretName: label.trim(), toolName: 'workspace', scope,
      });
    } catch (exc) {
      setError(exc?.message || 'Create failed');
    } finally {
      setBusy(false); setPhase('');
    }
  }, [source, templateId, file, label, scope, skillIds, tenantSkillIds, onRegistryCreated]);

  const runEdit = useCallback(async () => {
    if (!editingId) return;
    setBusy(true); setError(''); setPhase('seeding');
    try {
      const saved = await updateWorkspace(editingId, { skillIds, tenantSkillIds });
      onRegistryCreated?.({
        workspaceId: editingId,
        fileCount: saved?.fileCount || 0,
        sizeBytes: saved?.sizeBytes || 0,
        secretId: editingId,
        secretName: label.trim() || initialEntry?.secretName || '',
        toolName: 'workspace',
        scope,
        edited: true,
      });
    } catch (exc) {
      setError(exc?.message || 'Save failed');
    } finally {
      setBusy(false); setPhase('');
    }
  }, [editingId, skillIds, tenantSkillIds, label, scope, initialEntry, onRegistryCreated]);

  if (!open || !tenantId?.trim()) return null;

  // Chips + summary across both sources, in selection order.
  const selectedSkills = selectedNsIds.map((ns) => skillEntries.find((e) => e.id === ns)).filter(Boolean);
  const hasAnySkill = skills.length > 0 || tenantSkills.length > 0;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4"
      role="dialog" aria-modal="true" aria-labelledby="workspace-wizard-title"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onCancel?.(); }}
    >
      <div className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl">
        <div className="shrink-0 border-b border-gray-100 px-5 py-4">
          <h2 id="workspace-wizard-title" className="text-base font-semibold text-gray-900">
            {isNewEntry ? 'New shared workspace' : 'Edit shared workspace'}
          </h2>
          <p className="mt-1 text-xs text-gray-600">
            {isEdit
              ? 'Add or remove skills for this workspace. Source files are unchanged.'
              : <>Bootstrap a workspace from a template or your own files, optionally with skills. Visible to {scopeLabel(scope)}.</>}
          </p>
        </div>

        {/* Two-tab header — create only. Edit is a single skills surface. */}
        {!isEdit ? (
        <div className="shrink-0 border-b border-gray-100 bg-slate-50 px-5">
          <div className="flex gap-1">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => { if (t.key === 'create' && !startReady) return; setTab(t.key); setError(''); }}
                disabled={t.key === 'create' && !startReady}
                className={`-mb-px border-b-2 px-3 py-2.5 text-xs font-medium ${
                  tab === t.key ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'
                } disabled:opacity-40`}
              >
                {t.title}
              </button>
            ))}
          </div>
        </div>
        ) : null}

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 text-xs text-gray-800">
          {/* ── TAB 1: Start from ── */}
          {tab === 'start' ? (
            <div className="space-y-4">
              {/* EDIT mode: the source (template/upload) is immutable — show a
                  read-only summary, then ONLY the skills picker below. */}
              {isEdit ? (
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-[11px] text-gray-600">
                  <p className="font-medium text-gray-700">Editing “{initialEntry?.secretName || 'workspace'}”</p>
                  <p className="mt-0.5">
                    The source files can’t be changed here — add or remove
                    <span className="font-medium"> skills</span> below, then Save.
                  </p>
                </div>
              ) : (
              <>
              {/* Source toggle */}
              <div className="flex gap-2">
                {templates.length > 0 ? (
                  <button type="button" onClick={() => { setSource('template'); setError(''); }}
                    className={`flex-1 rounded-lg border-2 px-3 py-2 text-left transition-colors ${
                      source === 'template' ? 'border-blue-500 bg-blue-50/70' : 'border-gray-200 hover:border-gray-300'
                    }`}>
                    <div className="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
                      <LayoutTemplate className="h-4 w-4 text-blue-600" aria-hidden /> StepFlow template
                    </div>
                    <p className="mt-1 text-[11px] text-gray-600">Start from a ready-made structure.</p>
                  </button>
                ) : null}
                <button type="button" onClick={() => { setSource('upload'); setError(''); }}
                  className={`flex-1 rounded-lg border-2 px-3 py-2 text-left transition-colors ${
                    source === 'upload' ? 'border-blue-500 bg-blue-50/70' : 'border-gray-200 hover:border-gray-300'
                  }`}>
                  <div className="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
                    <UploadCloud className="h-4 w-4 text-blue-600" aria-hidden /> Upload my own
                  </div>
                  <p className="mt-1 text-[11px] text-gray-600">A .zip / .tar of your files.</p>
                </button>
              </div>

              {source === 'template' ? (
                <div className="space-y-2">
                  <select value={templateId} onChange={(e) => setTemplateId(e.target.value)}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100">
                    {templates.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                  </select>
                  <p className="text-[11px] text-gray-500">
                    {templates.find((t) => t.id === templateId)?.description
                      || 'A neutral starting structure — the builder adapts it to your task.'}
                  </p>
                </div>
              ) : (
                <>
                  <div
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={(e) => { e.preventDefault(); setDragOver(false); onPickFile(e.dataTransfer?.files?.[0]); }}
                    className={`flex flex-col items-center justify-center rounded-xl border-2 border-dashed px-6 py-8 text-center transition-colors ${
                      dragOver ? 'border-blue-500 bg-blue-50/70' : 'border-gray-300 hover:border-gray-400'
                    }`}>
                    <UploadCloud className="h-8 w-8 text-gray-400" aria-hidden />
                    <p className="mt-2 text-sm font-medium text-gray-700">Drag a .zip / .tar here, or</p>
                    <label className="mt-2 cursor-pointer rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50">
                      Choose file
                      <input type="file" accept=".zip,.tar,.tar.gz,.tgz,application/zip,application/x-tar,application/gzip"
                        className="hidden" onChange={(e) => onPickFile(e.target.files?.[0])} />
                    </label>
                    <p className="mt-2 text-[11px] text-gray-500">Up to 500 MB · {ALLOWED_EXTS.join(', ')}</p>
                  </div>
                  {file ? (
                    <div className="mt-2 flex items-center gap-2 rounded-lg bg-gray-50 px-3 py-2 text-[11px] text-gray-700">
                      <FileArchive className="h-4 w-4 text-blue-600" aria-hidden />
                      <span className="truncate font-medium">{file.name}</span>
                      <span className="ml-auto shrink-0 text-gray-500">{fmtBytes(file.size)}</span>
                    </div>
                  ) : null}
                </>
              )}
              </>
              )}

              {/* Skills — discover→multi-select via the shared CatalogBrowser.
                  Two tabs inside the picker: Imported (curated) + Your skills. */}
              {hasAnySkill ? (
                <div className="border-t border-gray-100 pt-3">
                  <div className="mb-1.5 flex items-center justify-between">
                    <span className="text-[11px] font-medium text-gray-700">
                      Skills <span className="font-normal text-gray-400">(optional)</span>
                    </span>
                    <div className="flex items-center gap-1.5">
                      {/* Bulk-select this network's own skills in one click
                          (doc/arch/vertical/SKILLS.md) — a frozen snapshot at
                          bake time, not a live link to the network. */}
                      {wsNetworkId && networkSkillNsIds.length > 0 ? (
                        <button type="button" onClick={addAllFromNetwork}
                          data-testid="workspace-add-network-skills"
                          title="Add this network's skills (a snapshot — later network edits won't change this workspace)"
                          className="inline-flex items-center gap-1 rounded-md border border-violet-300 bg-violet-50 px-2 py-0.5 text-[11px] font-medium text-violet-800 hover:bg-violet-100">
                          <Sparkles className="h-3 w-3" /> Add network skills ({networkSkillNsIds.length})
                        </button>
                      ) : null}
                      <button type="button" onClick={() => setSkillPickerOpen(true)}
                        data-testid="workspace-add-skills"
                        className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2 py-0.5 text-[11px] font-medium text-gray-700 hover:bg-gray-50">
                        <Sparkles className="h-3 w-3" /> Browse skills
                      </button>
                    </div>
                  </div>
                  {selectedSkills.length === 0 ? (
                    <p className="text-[11px] text-gray-400">None selected — the builder works without them.</p>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {selectedSkills.map((s) => (
                        <span key={s.id} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] ${
                          s.category === 'tenant' ? 'bg-violet-100 text-violet-900' : 'bg-emerald-100 text-emerald-900'
                        }`}>
                          {s.label}
                          <button type="button" onClick={() => toggleSkill(s, false)} aria-label={`Remove ${s.label}`}
                            className={s.category === 'tenant' ? 'text-violet-700 hover:text-violet-900' : 'text-emerald-700 hover:text-emerald-900'}>
                            <XIcon className="h-3 w-3" /></button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          ) : null}

          {/* ── TAB 2: Create ── */}
          {tab === 'create' ? (
            <div className="space-y-3">
              <label className="block font-medium text-gray-900">
                Workspace name <span className="text-red-500">*</span>
                <input type="text" value={label} onChange={(e) => setLabel(e.target.value)}
                  data-testid="workspace-name"
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
                  placeholder="e.g. Onboarding doc pack" autoFocus />
              </label>
              {!labelCheck.ok ? <p className="text-[11px] text-amber-800">{labelCheck.hint}</p> : null}

              <div className="rounded-lg bg-gray-50 px-3 py-2 text-[11px] text-gray-600">
                <p><span className="font-medium">Start from:</span>{' '}
                  {source === 'template'
                    ? `template “${templates.find((t) => t.id === templateId)?.label || templateId}”`
                    : `upload “${file?.name || '—'}”`}</p>
                {selectedSkills.length ? (
                  <p className="mt-0.5"><span className="font-medium">Skills:</span>{' '}
                    {selectedSkills.map((s) => s.label).join(', ')}</p>
                ) : null}
                <p className="mt-0.5"><span className="font-medium">Visible to:</span> {scopeLabel(scope)}</p>
              </div>
            </div>
          ) : null}

          {error ? <p className="mt-3 text-[11px] text-red-600">{error}</p> : null}
        </div>

        {/* Footer — ONE action per tab. On 'create' it IS the create+finish
            (no separate inline button, no success-then-Done screen). */}
        <div className="flex shrink-0 items-center justify-between gap-2 border-t border-gray-100 px-5 py-3">
          <button type="button" onClick={onCancel} disabled={busy}
            className="rounded-md px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-50">
            Cancel
          </button>
          {isEdit ? (
            <button type="button" onClick={runEdit} disabled={busy}
              data-testid="workspace-save-skills"
              className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              {busy ? 'Saving…' : 'Save skills'}
            </button>
          ) : tab === 'start' ? (
            <button type="button" onClick={() => setTab('create')} disabled={!startReady}
              data-testid="workspace-next"
              className="rounded-md bg-blue-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-40">Next</button>
          ) : (
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setTab('start')} disabled={busy}
                className="rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">Back</button>
              <button type="button" onClick={runCreate} disabled={busy || !labelCheck.ok || !startReady}
                data-testid="workspace-create"
                className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                {phase === 'seeding' ? 'Creating…' : phase === 'uploading' ? 'Uploading…' : phase === 'unpacking' ? 'Unpacking…'
                  : source === 'template' ? 'Create from template' : 'Upload & create'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Skills picker — the shared discover→select component, multi-select. */}
      <CatalogBrowser
        open={skillPickerOpen}
        title="Add skills"
        subtitle="Imported open-source skills and your tenant's own — read the details, then add the ones that fit."
        entries={skillEntries}
        tabs={skillTabs}
        loadDetail={loadSkillDetail}
        multiSelect
        selectedIds={selectedNsIds}
        onToggle={toggleSkill}
        onDone={() => setSkillPickerOpen(false)}
        onCancel={() => setSkillPickerOpen(false)}
        maxSelected={MAX_SKILLS}
        testidPrefix="skill-catalog"
      />
    </div>
  );
}
